import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { Params, RouterLink } from "@angular/router";
import { NgbTooltipModule } from "@ng-bootstrap/ng-bootstrap";
import { ComponentOutletInjectorModule, DynamicIoDirective } from "ng-dynamic-component";
import { ActionGroupComponent } from "../../action-group/action-group.component";
import { DefaultActionsGroupComponent } from "../../default-actions-group/default-actions-group.component";
import { TableAction } from "../../sortable-table/sortable-table.component";
import {
  DefaultTableAction,
  MoveAction,
  MultiI,
  MultiLevelTableColumn,
  MultiWrapper,
} from "../multi-level-table.component";

@Component({
  selector: "tr[app-table-row]",
  standalone: true,
  imports: [
    ActionGroupComponent,
    DefaultActionsGroupComponent,
    NgbTooltipModule,
    DynamicIoDirective,
    ComponentOutletInjectorModule,
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: "./table-row.component.html",
  styleUrl: "./table-row.component.scss",
})
export class TableRowComponent<MW extends MultiWrapper<A, B>, A extends MultiI, B>
  implements OnChanges
{
  @Input() item!: MW;
  @Input() columns: MultiLevelTableColumn<A, B>[] = [];

  @Input() actions: TableAction<A | B>[] = [];
  @Input() defaultActions: DefaultTableAction<A | B>[] = [];

  @Input() itemLabel: string = "";
  @Input() itemName: string = "";

  @Input() isHeader = false;

  @Input() showCollapseButton = false;
  @Input() enableSelect: boolean = false;
  @Input() disableSelectButton: boolean = false;
  @Input() isCollapsed = false;
  @Input() firstColumnColspan = 1;
  @Input() emptyColumnsAtStart = 0;
  @Input() whiteColumnsAtStart = 0;

  @Output() addEvent = new EventEmitter<A | B>();
  @Output() moveEvent = new EventEmitter<{
    action: MoveAction<A | B>;
    item: A | B;
    direction: number;
  }>();
  @Output() deleteEvent = new EventEmitter<A | B>();
  @Output() collapseEvent = new EventEmitter();
  @Output() selectEvent = new EventEmitter();

  @Input() isLast!: boolean;
  @Input() isFirst!: boolean;

  protected fakeArray = new Array(this.emptyColumnsAtStart);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["emptyColumnsAtStart"]) {
      this.fakeArray = new Array(this.emptyColumnsAtStart);
    }
  }

  isMultiI(row: A | B): row is A {
    return row instanceof Object && "Children" in row;
  }

  isNotMultiI(row: A | B): row is B {
    return !this.isMultiI(row);
  }

  onCellClick(col: MultiLevelTableColumn<A, B>, row: A | B): void {
    if (col.cellClick) {
      col.cellClick(row);
    }
  }

  getRouterLink(col: MultiLevelTableColumn<A, B>, row: A | B): string | null {
    if (typeof col.routerLink === "function") {
      // If action.routerLink is a function, call it and return the result
      return col.routerLink(row);
    } else {
      // If action.routerLink is a string, return it as is
      return col.routerLink ?? null;
    }
  }

  getQueryParams(col: MultiLevelTableColumn<A, B>, row: A | B): Params | null {
    if (typeof col.queryParams === "function") {
      // If action.queryParams is a function, call it and return the result
      return col.queryParams(row);
    } else {
      return col.queryParams ?? null;
    }
  }

  toggleSelectItem() {
    this.item.setSelected(!this.item.isSelected);
    this.selectEvent.emit(this.item);
  }

  isItemSelected() {
    return this.item.isSelected;
  }

  isIndeterminate() {
    return this.item.wrapperChildren?.some((child) => child.isSelected) && !this.item.isSelected;
  }
}
